import {
  SET_BENEFITS,
  SET_DETAIL_VOUCHER,
  SET_IS_FETCHING_PAYMENT_CHANNEL,
  SET_IS_LOADING,
  SET_LAST_QUERY,
  SET_MECHANISMS,
  SET_PAGINATION_DATA_VOUCHER,
  SET_PAYMENT_CHANNEL,
  SET_RESET,
  SET_TARGETS,
  SET_VOUCHER_DATA,
} from 'storeContext/actionsType/voucher'
import type {
  VoucherDetailType,
  VoucherListItemType,
  VoucherPaginationType,
  VoucherStateType,
} from 'storeContext/reducers/voucher'

import {
  getBenefits,
  getMechanisms,
  getPaymentChannel,
  getTargets,
  getVoucher,
  getVoucherDetail,
  postVoucher,
  putVoucherActive,
  putVoucherDetail,
} from 'utils/api'
import { toastFailed, toastSuccess } from 'utils/toast'
import type { PromotionQueryType } from 'utils/apiList/promotion'

interface VoucherPlainActionType {
  type: string
  payload?: unknown
}

type VoucherGetStateType = () => { stateVoucher: VoucherStateType }
type VoucherThunkType = (dispatch: VoucherDispatchType, getState: VoucherGetStateType) => void
type VoucherDispatchType = (action: VoucherPlainActionType | VoucherThunkType) => void

interface VoucherListResponseType {
  content: VoucherListItemType[]
  size: number
  totalElements: number
  totalPages: number
  number: number
  numberOfElements: number
}

interface VoucherActiveItemType {
  voucher_id: string | number
  voucher_active?: boolean
}

interface PaymentChannelResponseType {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Preserve API response key.
  payment_channel_EWallet: unknown[]
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Preserve API response key.
  payment_channel_Virtual_Account: unknown[]
}

export function setDetailVoucher(payload: VoucherDetailType) {
  return {
    type: SET_DETAIL_VOUCHER,
    payload,
  }
}

export function setIsLoading(payload: boolean) {
  return {
    type: SET_IS_LOADING,
    payload,
  }
}

export function setPaginationData(payload: VoucherPaginationType) {
  return {
    type: SET_PAGINATION_DATA_VOUCHER,
    payload,
  }
}
export function setVoucherData(payload: VoucherListItemType[]) {
  return {
    type: SET_VOUCHER_DATA,
    payload,
  }
}
export function setLastQuery(payload: PromotionQueryType) {
  return {
    type: SET_LAST_QUERY,
    payload,
  }
}
export function setMechanisms(payload: unknown[]) {
  return {
    type: SET_MECHANISMS,
    payload,
  }
}
export function setBenefits(payload: unknown[]) {
  return {
    type: SET_BENEFITS,
    payload,
  }
}
export function setTargets(payload: unknown[]) {
  return {
    type: SET_TARGETS,
    payload,
  }
}
export function setPaymentChannelList(payload: unknown[]) {
  return {
    type: SET_PAYMENT_CHANNEL,
    payload,
  }
}
export function setIsFetchingPaymentChannel(payload: boolean) {
  return {
    type: SET_IS_FETCHING_PAYMENT_CHANNEL,
    payload,
  }
}
export function reset() {
  return {
    type: SET_RESET,
  }
}

export const actGetVouchers =
  (query: PromotionQueryType = {}) =>
  (dispatch: VoucherDispatchType) => {
    dispatch(setIsLoading(true))
    getVoucher(query)
      .then(({ data }: { data: VoucherListResponseType }) => {
        const dataVoucher = data.content
        const pagination = {
          pageSize: data.size,
          totalData: data.totalElements,
          totalPage: data.totalPages,
          currentPage: data.number,
          currentItem: data.numberOfElements,
        }
        dispatch(setVoucherData(dataVoucher))
        dispatch(setPaginationData(pagination))
        dispatch(setLastQuery(query))
      })
      .finally(() => {
        dispatch(setIsLoading(false))
      })
  }

export const actGetDataType = () => (dispatch: VoucherDispatchType) => {
  dispatch(setIsLoading(true))
  const pendingPromises = [getBenefits(), getTargets(), getMechanisms()]
  Promise.all(pendingPromises)
    .then(([{ data: dataBenefits }, { data: dataTargets }, { data: _dataMechanisms }]) => {
      dispatch(setBenefits(dataBenefits))
      dispatch(setTargets(dataTargets))
    })
    .finally(() => {
      dispatch(setIsLoading(false))
    })
}

export const actGetPaymentChannel = () => async (dispatch: VoucherDispatchType) => {
  dispatch(setIsFetchingPaymentChannel(true))
  try {
    const res = (await getPaymentChannel()) as { data: PaymentChannelResponseType }
    const newList = [
      ...res.data.payment_channel_EWallet,
      ...res.data.payment_channel_Virtual_Account,
    ]
    dispatch(setPaymentChannelList(newList))
  } catch (_error: unknown) {
    toastFailed('Gagal mendapatkan list payment channel')
  } finally {
    dispatch(setIsFetchingPaymentChannel(false))
  }
}

export const actHandleAddVoucher =
  (payload: Record<string, unknown> | FormData, cb: () => void = () => undefined) =>
  (dispatch: VoucherDispatchType) => {
    dispatch(setIsLoading(true))
    postVoucher(payload)
      .then(() => {
        cb()
        setTimeout(() => {
          toastSuccess('Voucher berhasil ditambahkan')
        }, 300)
      })
      .catch(() => {
        toastFailed('Voucher gagal ditambahkan')
      })
      .finally(() => {
        dispatch(setIsLoading(false))
      })
  }

export const actHandleChangeActive =
  (data: VoucherActiveItemType) =>
  (dispatch: VoucherDispatchType, getState: VoucherGetStateType) => {
    dispatch(setIsLoading(true))
    putVoucherActive(data.voucher_id, { is_active: !data.voucher_active })
      .then(() => {
        const { lastQuery } = getState().stateVoucher
        dispatch(actGetVouchers(lastQuery))
        toastSuccess('Voucher berhasil diupdate')
      })
      .finally(() => {
        dispatch(setIsLoading(false))
      })
  }

export const actHandleGetVoucherData = (id: string | number) => (dispatch: VoucherDispatchType) => {
  dispatch(setIsLoading(true))
  getVoucherDetail(id)
    .then(({ data }) => {
      dispatch(setDetailVoucher(data))
    })
    .finally(() => {
      dispatch(setIsLoading(false))
    })
}

export const actHandleEditVoucher =
  (
    id: string | number,
    payload: Record<string, unknown> | FormData,
    cb: () => void = () => undefined,
  ) =>
  (dispatch: VoucherDispatchType) => {
    dispatch(setIsLoading(true))
    putVoucherDetail(id, payload)
      // eslint-disable-next-line sonarjs/no-identical-functions -- Preserve legacy success callback.
      .then(() => {
        cb()
        setTimeout(() => {
          toastSuccess('Voucher berhasil ditambahkan')
        }, 300)
      })
      .catch(() => {
        toastFailed('Voucher gagal ditambahkan')
      })
      .finally(() => {
        dispatch(setIsLoading(false))
      })
  }
